/**
 * Map a Google Places `address_components` array onto RentRisk's address fields.
 *
 * Extracted from AddProperty.vue's initAutocomplete so the mapping is testable:
 * RAC-10462 came in because `subpremise` (the unit number) was parsed nowhere,
 * so a unit typed into the lookup box was silently dropped on the way to the form.
 *
 * `city`, `unit`, `zip_code`, `county` and `state` come back as null when the place
 * carries no such component. Callers decide which nulls overwrite an existing value
 * — a missing unit must never clear one the user typed by hand.
 */
export const parseGooglePlaceAddress = (addressComponents = []) => {
    let street = null
    let route = null
    let unit = null
    let city = null
    let county = null
    let state = null
    let zipCode = null

    addressComponents.forEach((component) => {
        const componentType = component.types[0]
        switch (componentType) {
            case 'street_number': {
                street = `${component.long_name}`
                break
            }
            case 'route': {
                route = component.short_name
                break
            }
            case 'subpremise': {
                unit = component.long_name
                break
            }
            case 'postal_code': {
                zipCode = `${component.long_name}`
                break
            }
            case 'locality':
                city = component.long_name
                break
            case 'neighborhood':
                if (city === null) {
                    city = component.long_name
                }
                break
            case 'administrative_area_level_3':
                if (city === null) {
                    city = component.long_name
                }
                break
            case 'administrative_area_level_2':
                county = component.long_name
                break
            case 'administrative_area_level_1': {
                state = `${component.short_name}`
                break
            }
            case 'country': {
                // US territories have no administrative_area_level_1; the country
                // code is what RentRisk stores as the state.
                if (['VI', 'AS', 'GU', 'PR', 'MP'].includes(component.short_name)) {
                    state = `${component.short_name}`
                }
                break
            }
        }
    })

    return {
        address_1: street === null || route === null ? null : `${street} ${route}`,
        unit,
        city,
        county,
        state,
        zip_code: zipCode,
    }
}
